// Pure gating/label helpers for the POS push card, split out so they are
// testable without mounting the component.

export function posCardShows(client, posCode, workLocation) {
    const byPos = (client && client.pos_sidebar_work_locations_by_pos) || {}
    return (byPos[posCode] || []).includes(workLocation)
}

export function pushButtonLabel(title, isSynced, inFlight) {
    if (inFlight) return 'Pushing…'
    return isSynced ? `Re-push to ${title}` : `Push to ${title}`
}

// FlexePOS has its own push endpoint; SynergySuite and CrunchTime share one,
// selected by the `pos` body field.
export function pushEndpoint(posCode) {
    return posCode === 'flexepos' ? 'pos-actions/flexepos-sync' : 'pos-actions/new-hire-push'
}

const FINISHED_STATUSES = ['success', 'error']

// The finished update a queued push (or terminate) wrote: one that wasn't there
// before, of `action` when given. Null while it is still pending.
export function findPushResult(updates, seenIds, action) {
    return (updates || []).find(u => (
        !seenIds.includes(u.id) && FINISHED_STATUSES.includes(u.status) && (!action || u.action === action)
    )) || null
}

// After check `attempt` finds nothing: the delay before the next check (null to
// stop) and whether this miss used up the fast checks. With a slow interval it
// keeps checking at that pace instead of stopping.
export function nextPollDelay(attempt, maxAttempts, intervalMs, slowIntervalMs) {
    if (attempt < maxAttempts) return {delay: intervalMs, timedOut: false}
    return {delay: slowIntervalMs || null, timedOut: attempt === maxAttempts}
}

export function posUpdateErrorMessage(update) {
    const resp = (update && update.api_response) || {}
    if (resp.error) return resp.error
    return (resp.errors || []).map(e => (typeof e === 'string' ? e : e.issue)).filter(Boolean).join('\n')
}

// The terminate button is CrunchTime-only and gated by the client flag. The
// server enforces the role at the employee's company; this only hides the button.
export function canTerminateInPos(client, posCode, isSuperuser, hasRoleAtCompany) {
    if (posCode !== 'crunchtime') return false
    if (!client || !client.crunchtime_sidebar_terminate) return false
    return Boolean(isSuperuser || hasRoleAtCompany)
}

// toastr renders HTML (escapeHtml is off), and these messages carry CrunchTime text.
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

// The terminate task's history row, in the shape terminateSummary reads.
export function terminateResultFromUpdate(update) {
    const resp = (update && update.api_response) || {}
    const failed = Boolean(update) && update.status === 'error'
    let errors = resp.errors || []
    if (failed && !errors.length) errors = [posUpdateErrorMessage(update) || 'See the history below for details.']
    return {
        terminated: !failed && resp.terminated === true,
        termination_date: resp.termination_date || null,
        warnings: resp.warnings || [],
        errors,
    }
}

// Title and lines come back HTML-escaped, ready for $bus.show*.
export function terminateSummary(result) {
    const errors = (result && result.errors) || []
    const warnings = (result && result.warnings) || []
    const summary = (level, title, lines) => ({level, title: escapeHtml(title), lines: lines.map(escapeHtml)})
    if (errors.length) {
        return summary('error', 'CrunchTime terminate failed', errors)
    }
    if (warnings.length) {
        return summary('warning', 'CrunchTime terminate complete (with warnings)', warnings)
    }
    return summary('info', 'Terminated in CrunchTime', [`Termination date ${result.termination_date}`])
}
